import { createFileRoute } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { EstimateForm } from "#/components/shared/EstimateForm";
import { HomeFAQ } from "#/components/pages/home";
import { PageHeader } from "#/components/shared/PageHeader";
import { SectionLabel } from "#/components/shared/SectionLabel";
import { HEADER } from "#/lib/images";
import { QUOTE_INCLUDES, QUOTE_WHY_FREE } from "#/lib/constants";

const TITLE = "Get a Free Quote | Blue Rock Concrete";
const DESCRIPTION =
  "Tell us about your concrete project and get a free, no-pressure written estimate, usually on-site within the week. No deposit, no obligation.";

export const Route = createFileRoute("/quote")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://bluerockconcreteco.com/quote" },
    ],
    links: [{ rel: "canonical", href: "https://bluerockconcreteco.com/quote" }],
    // No FAQPage JSON-LD here on purpose. /reviews is the canonical home of the
    // FAQ (it owns the #faq anchor the nav and footer point at) and emits the
    // schema. Emitting the identical FAQPage entity from two URLs splits the
    // rich-result signal between them and reads as duplication. The FAQ still
    // renders below for the reader, it just is not claimed twice.
  }),
  component: QuotePage,
});

function QuotePage() {
  return (
    <main>
      <PageHeader
        eyebrow="Free Estimate"
        title="Get your project quoted"
        intro="Tell us about your project and get a free, written estimate, usually on-site within the week."
        image={HEADER.quote.src}
        imageMobile={HEADER.quote.mobile}
        imageAlt={HEADER.quote.alt}
      />

      {/* Estimate form — customer-facing forms are always light, never dark
          (see memory/lessons.md 2026-07-25). Mirrors the home FinalCTA. */}
      <section id="contact" className="scroll-mt-24 bg-light-bg py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <EstimateForm />
        </div>
      </section>

      {/* What's included */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionLabel>What&apos;s Included</SectionLabel>
          <h2 className="mt-4 max-w-2xl font-heading text-3xl font-black uppercase leading-[1.05] tracking-tight text-graphite sm:text-4xl">
            Every free estimate covers this
          </h2>

          <div className="mt-12 grid divide-y divide-graphite/10 border border-graphite/10 sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4">
            {QUOTE_INCLUDES.map((item) => (
              <div key={item.title} className="p-7 lg:p-8">
                <div className="flex h-10 w-10 items-center justify-center bg-flame text-white">
                  <Check className="h-5 w-5" strokeWidth={3} />
                </div>
                <h3 className="mt-5 font-heading text-lg font-bold text-graphite">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-concrete-dark">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why the estimate is free */}
      <section className="bg-graphite py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <SectionLabel tone="light" className="justify-center">
            Why It&apos;s Free
          </SectionLabel>
          <h2 className="mt-4 font-heading text-2xl font-black uppercase leading-tight tracking-tight text-white sm:text-3xl">
            No cost. No pressure. No catch.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/75">
            {QUOTE_WHY_FREE}
          </p>
        </div>
      </section>

      <HomeFAQ />
    </main>
  );
}
